import { Injectable, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { FieldTest } from '../data-models/field-test.model';
import { FieldTestType } from '../data-models/field-test-type.model';
import { FieldTestStatus } from '../data-models/field-test-status.model';
import { FieldTestRequest } from '../request-models/field-test-request.model';
import { FieldTestStats } from '../response-models/field-test-stats.model';
import { FIELD_TEST_TYPE_OPTIONS } from '../../shared/constants/field-test-options';
import { toFieldTestRequest } from '../utils/field-test-utils';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';

/** Filters and paging for the field test list. */
export interface FieldTestListOptions {
  testType?: string;
  status?: string;
  aircraftId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

const EMPTY_STATS: FieldTestStats = { passed: 0, failed: 0, pending: 0, total: 0 };

/**
 * Field tests (pre-flight, post-flight, equipment, calibration): `/field-tests[/:id]` and `/field-tests/stats`
 * (hub `user-field-tests-controller.ts`). Paged with `page` / `limit`. Custom test submissions live in
 * `FieldTestSubmissionService`, templates in `FieldTestTemplateService`.
 *
 * Holds the last loaded page and the stats as signal state. Failures are recorded in `error` (the hub's
 * message) and the Promise helpers resolve `null` / `false`.
 */
@Injectable({
  providedIn: 'root'
})
export class FieldTestService extends BaseService<FieldTest> {
  private fieldTestsSignal = signal<FieldTest[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private statsSignal = signal<FieldTestStats>(EMPTY_STATS);

  readonly fieldTests = computed(() => this.fieldTestsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  readonly passedCount = computed(() => this.statsSignal().passed);
  readonly failedCount = computed(() => this.statsSignal().failed);
  readonly pendingCount = computed(() => this.statsSignal().pending);

  readonly testTypeOptions = FIELD_TEST_TYPE_OPTIONS;

  constructor() {
    super('field-tests', FieldTest);
  }

  /** Load a page of field tests (default: the first 100) into `fieldTests`. */
  async loadFieldTests(options?: FieldTestListOptions): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const query = { ...options, page: options?.page || 1, limit: options?.limit || 100 };
      const page = await firstValueFrom(this.getPage({ query }));
      this.fieldTestsSignal.set(page.items);
    } catch (error: unknown) {
      this.recordError(error, 'Failed to load field tests');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Load the counts by status. Failures are logged and leave the stats unchanged. */
  async loadStats(): Promise<void> {
    try {
      this.statsSignal.set(await firstValueFrom(this.request('GET', { path: 'stats' }, toStats)));
    } catch (error: unknown) {
      console.error('Error loading field test stats:', ApiError.fromHttpError(error).message);
    }
  }

  getFieldTests(): FieldTest[] {
    return this.fieldTestsSignal();
  }

  getFieldTestById(id: string): FieldTest | undefined {
    return this.fieldTestsSignal().find(t => t.id === id);
  }

  /** Fetch one field test; resolves `null` on failure. */
  async fetchFieldTestById(id: string): Promise<FieldTest | null> {
    try {
      return await firstValueFrom(this.get(id));
    } catch (error: unknown) {
      console.error('Error fetching field test:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** Create a field test and prepend it to `fieldTests`; resolves `null` on failure. */
  async createFieldTest(dto: FieldTestRequest): Promise<FieldTest | null> {
    return this.track('Failed to create field test', async () => {
      const created = await firstValueFrom(this.create(toFieldTestRequest(dto)));
      if (created) {
        this.fieldTestsSignal.update(tests => [created, ...tests]);
      }
      void this.loadStats();
      return created;
    });
  }

  /** Update a field test and replace it in `fieldTests`; resolves `null` on failure. */
  async updateFieldTest(id: string, dto: FieldTestRequest): Promise<FieldTest | null> {
    return this.track('Failed to update field test', async () => {
      const updated = await firstValueFrom(this.update(id, toFieldTestRequest(dto)));
      if (updated) {
        this.fieldTestsSignal.update(tests => tests.map(t => (t.id === id ? updated : t)));
      }
      void this.loadStats();
      return updated;
    });
  }

  /** Delete a field test and remove it from `fieldTests`; resolves `false` on failure. */
  async deleteFieldTest(id: string): Promise<boolean> {
    const deleted = await this.track('Failed to delete field test', async () => {
      await firstValueFrom(this.delete(id));
      this.fieldTestsSignal.update(tests => tests.filter(t => t.id !== id));
      void this.loadStats();
      return true;
    });
    return deleted ?? false;
  }

  getStatusText(status: FieldTestStatus): string {
    switch (status) {
      case 'passed': return 'Passed';
      case 'failed': return 'Failed';
      case 'pending': return 'Pending';
      default: return status;
    }
  }

  getTestTypeBadgeClass(type: FieldTestType | null | undefined): string {
    switch (type) {
      case 'pre-flight': return 'type-preflight';
      case 'post-flight': return 'type-postflight';
      case 'equipment': return 'type-equipment';
      case 'calibration': return 'type-calibration';
      default: return 'bg-secondary';
    }
  }

  getTestTypeLabel(type: FieldTestType | null | undefined): string {
    return this.testTypeOptions.find(t => t.value === type)?.label || type || '';
  }

  getPassedChecklistCount(test: FieldTest): number {
    return (test.checklist ?? []).filter(c => c.passed).length;
  }

  /** Runs a write with the loading flag set; a failure is recorded in `error` and resolves `null`. */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action();
    } catch (error: unknown) {
      this.recordError(error, fallback);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  private recordError(error: unknown, fallback: string): void {
    const apiError = ApiError.fromHttpError(error);
    console.error(`[FieldTestService] ${fallback}:`, apiError.message);
    this.errorSignal.set(apiError.errors[0] ?? fallback);
  }
}

function toStats(data: unknown): FieldTestStats {
  const source = isRecord(data) ? data : {};
  return {
    passed: countOrZero(source['passed']),
    failed: countOrZero(source['failed']),
    pending: countOrZero(source['pending']),
    total: countOrZero(source['total']),
  };
}
